/**
 * useDimensionOptions.js — Custom hook for Brand/SKU/Channel/Location dropdowns.
 *
 * Replaces the identical fetch blocks copy-pasted into:
 *   - DailyForecastControl.js
 *   - PromotionalAnalysisControl.js
 *   - AdHocForecastControl.js
 *   - ScenarioPanel.js
 *
 * All four panels can now share a single source of truth.
 */

import { useState, useEffect } from 'react';
import { fetchDimensionOptions, fetchSkusByBrand } from '../services_1/apiClient';

const useDimensionOptions = (initialBrand = '') => {
  const [options, setOptions] = useState({
    brands: [],
    skus: [],
    channels: [],
    locations: [],
    regions: [],
    locationRegionMap: {},
    skuBrandMap: {},
  });
  const [selectedBrand, setSelectedBrand] = useState(initialBrand || '');
  const [optionsLoading, setOptionsLoading] = useState(true);

  useEffect(() => {
    setSelectedBrand(initialBrand || '');
  }, [initialBrand]);

  // Fetch brands, channels, locations, and regions once on mount
  useEffect(() => {
    const load = async () => {
      setOptionsLoading(true);
      try {
        const data = await fetchDimensionOptions();
        setOptions({
          brands: data.brands || [],
          skus: data.skus || [],
          channels: data.channels || [],
          locations: data.locations || [],
          regions: data.regions || [],
          locationRegionMap: data.locationRegionMap || {},
          skuBrandMap: data.skuBrandMap || {},
        });
      } catch (err) {
        console.error('Failed to load dimension options:', err);
      } finally {
        setOptionsLoading(false);
      }
    };
    load();
  }, []);

  // Re-fetch SKUs when brand changes
  useEffect(() => {
    if (!selectedBrand) {
      return;
    }

    const loadSkus = async () => {
      try {
        const skus = await fetchSkusByBrand(selectedBrand);
        setOptions(prev => {
          const nextSkus = Array.isArray(skus) && skus.length > 0 ? skus : prev.skus;
          return { ...prev, skus: nextSkus };
        });
      } catch (err) {
        console.error('Failed to load SKUs:', err);
      }
    };
    loadSkus();
  }, [selectedBrand]);

  return { options, setOptions, selectedBrand, setSelectedBrand, optionsLoading };
};

export default useDimensionOptions;
